/**
 * AdminPortal: the unified /admin portal, on mockup carbon/01's brief and
 * carbon/02's tables.
 *
 *   1. The brief (AdminPortalHeader): composeAdminBrief's action-first h1 and
 *      lede through BriefHero, a tertiary that opens the leading queue, the
 *      provenance line, and the "Open items" tile with a bar per queue.
 *   2. Line tabs (Organizations, People, Monitored Tenants), each with its
 *      pending count as a tag.
 *   3. The selected tab's pane: a DataTable with status tabs, its drawer and
 *      its modals.
 *
 * State (active tab, drawer, filters) lives in the URL via
 * `useAdminPortalState`, so deep links and the back button work.
 *
 * Known counts only: the brief, the tile and the tab tags read the three
 * queue hooks, and a queue that is loading or failed is unknown. While any is
 * loading the brief shows its placeholder; when one failed the brief says so
 * instead of composing a fabricated all-clear from a 0. The hooks share their
 * TanStack cache entries with the tabs, so the numbers never drift.
 */

import { useEffect, useState } from 'react'

import { composeAdminBrief } from '@/lib/admin-brief'
import type { ActionBrief } from '@/lib/brief-shape'
import { useAdminPortalState, type AdminTab } from '@/hooks/useAdminPortalState'
import { useOrganizations } from '@/hooks/useOrganizations'
import { useUsers } from '@/hooks/useUsers'
import { useTenants } from '@/hooks/useTenants'

import { Button } from '@/components/carbon/Button'
import { IconArrowRight } from '@/components/carbon/icons'
import AdminPortalHeader from '@/components/AdminPortal/AdminPortalHeader'
import { AdminPillTabs, ADMIN_PANEL_ID_PREFIX, ADMIN_TAB_ID_PREFIX } from '@/components/AdminPortal/AdminPillTabs'
import { formatClock } from '@/components/AdminPortal/admin-format'
import OrganizationsTab from '@/components/AdminPortal/tabs/OrganizationsTab'
import PeopleTab from '@/components/AdminPortal/tabs/PeopleTab'
import MonitoredTenantsTab from '@/components/AdminPortal/tabs/MonitoredTenantsTab'

/** The filter key each tab uses for its pending queue (the tabs' FilterKey values). */
const PENDING_FILTER: Record<AdminTab, string> = {
  organizations: 'pending',
  people: 'pending',
  tenants: 'needs-consent',
}

const OPEN_QUEUE_LABEL: Record<AdminTab, string> = {
  organizations: 'Show the pending organizations',
  people: 'Show the people not yet welcomed',
  tenants: 'Show the tenants needing consent',
}

/**
 * The queue the brief's headline leads with: the highest count, ties in the
 * stable organizations, people, tenants order (the same order the composer
 * uses), so the tertiary opens the queue the h1 names.
 */
function leadingQueue(counts: Record<AdminTab, number>): AdminTab | null {
  const order: AdminTab[] = ['organizations', 'people', 'tenants']
  let best: AdminTab | null = null
  for (const tab of order) {
    if (counts[tab] > 0 && (best === null || counts[tab] > counts[best])) best = tab
  }
  return best
}

/** The brief when a queue did not load: no total, so no verdict built on a 0. */
function unknownQueueBrief(failed: string[]): ActionBrief {
  const names = failed.length === 1 ? failed[0] : `${failed.slice(0, -1).join(', ')} and ${failed[failed.length - 1]}`
  return {
    headline: 'Reload the page to count every queue.',
    lede: `**The ${names} ${failed.length === 1 ? 'list' : 'lists'} did not load**, so Guardian cannot total the open items.`,
  }
}

export default function AdminPortal() {
  const state = useAdminPortalState()

  // Page-level pending-count: matches the nav-badge query in Layout.tsx
  // (same key shape, pageSize=200) so the badge and the tile agree.
  const orgs = useOrganizations({ pageSize: 200 })

  // People pending-count: shared TanStack cache with PeopleTab (the same
  // `users.list` query), so this read never triggers a second fetch.
  const users = useUsers()

  // Tenants needing consent: shared cache with MonitoredTenantsTab;
  // `pendingConsentCount` is the value the tab's filter count uses.
  const tenants = useTenants()

  const orgsKnown = !orgs.isLoading && !orgs.error
  const usersKnown = !users.isLoading && !users.isError
  const tenantsKnown = !tenants.isLoading && !tenants.isError
  const isLoading = orgs.isLoading || users.isLoading || tenants.isLoading
  const failed = [
    orgs.error ? 'organizations' : null,
    users.isError ? 'people' : null,
    tenants.isError ? 'tenants' : null,
  ].filter((name): name is string => name !== null)
  const allKnown = orgsKnown && usersKnown && tenantsKnown

  const pendingOrgs = orgs.pendingCount
  const pendingUsers = users.pendingCount
  const tenantsNeedingConsent = tenants.pendingConsentCount

  const brief =
    !isLoading && failed.length > 0
      ? unknownQueueBrief(failed)
      : composeAdminBrief({ pendingOrgs, pendingUsers, tenantsNeedingConsent })
  const totalPending = allKnown ? pendingOrgs + pendingUsers + tenantsNeedingConsent : null

  // The provenance time is when the counts last changed, not every render.
  const [countedAt, setCountedAt] = useState<Date | null>(null)
  useEffect(() => {
    if (allKnown) setCountedAt(new Date())
  }, [allKnown, pendingOrgs, pendingUsers, tenantsNeedingConsent])

  const leading = allKnown
    ? leadingQueue({ organizations: pendingOrgs, people: pendingUsers, tenants: tenantsNeedingConsent })
    : null

  return (
    <div>
      <AdminPortalHeader
        brief={brief}
        totalPending={totalPending}
        isLoading={isLoading}
        breakdown={allKnown ? { orgs: pendingOrgs, people: pendingUsers, tenants: tenantsNeedingConsent } : null}
        provenance={allKnown && countedAt ? `Counted by Guardian at ${formatClock(countedAt)} · Platform scope` : undefined}
        secondaryAction={
          leading ? (
            <Button
              kind="tertiary"
              icon={<IconArrowRight />}
              onClick={() => state.mergePivot({ tab: leading, filter: PENDING_FILTER[leading], orgFilter: null })}
            >
              {OPEN_QUEUE_LABEL[leading]}
            </Button>
          ) : undefined
        }
      />

      <div className="mt-12">
        <AdminPillTabs
          activeTab={state.tab}
          onTabChange={state.setTab}
          counts={{
            organizations: orgsKnown ? pendingOrgs : null,
            people: usersKnown ? pendingUsers : null,
            tenants: tenantsKnown ? tenantsNeedingConsent : null,
          }}
        />
      </div>

      <div
        id={`${ADMIN_PANEL_ID_PREFIX}${state.tab}`}
        role="tabpanel"
        aria-labelledby={`${ADMIN_TAB_ID_PREFIX}${state.tab}`}
        className="mt-8 min-w-0"
      >
        {state.tab === 'organizations' && <OrganizationsTab />}
        {state.tab === 'people' && <PeopleTab />}
        {state.tab === 'tenants' && <MonitoredTenantsTab />}
      </div>
    </div>
  )
}
